import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  IBooking,
  IStopTimeDisplay,
  ITrip,
  ITripDetail,
  ITripStop,
  IUserDriver,
  IVehicle,
  IBase,
} from 'src/app/models';
import { Colors, ExpeditionStatus, ExpeditionType } from 'src/app/models/enums';
import {
  StorageService,
  TripsService,
  UsersService,
  UtilsService,
} from 'src/app/services';
import { LanguageService } from 'src/app/services/language/language.service';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { TooltipPosition } from 'src/app/shared/tooltip/tooltip-position';
import { initialTripsState } from 'src/app/store/initialisation/trips';
import { images } from 'src/images';
import {
  DEFAULT_TRIP_STATUS,
  ITripsFilters,
  TRIP_FILTER_KEYS,
  deserializeTripsFilters,
  hasAnyFilterKey,
  serializeTripsFilters,
} from 'src/app/filters';

@Component({
    selector: 'app-trip-modal',
    templateUrl: './trip-modal.component.html',
    styleUrl: './trip-modal.component.scss',
    standalone: false
})
export class TripModalComponent implements OnInit, OnDestroy {
  @Input() trips = initialTripsState;
  @Input() showTripDetails: boolean;
  @Input() selectedTrip: ITrip;
  @Input() tripDetails: ITripDetail;
  @Input() searching: boolean = false;
  @Output() getTripEventEmitter = new EventEmitter<number>();
  @Output() refreshTripsEventEmitter = new EventEmitter();
  @Output() clearMap = new EventEmitter();
  @Output() showChangeDriverEventEmitter = new EventEmitter();
  @Output() showChangeVehicleEventEmitter = new EventEmitter();
  @Output() showEditCommentEventEmitter = new EventEmitter();
  @Output() showViewBookingsEventEmitter = new EventEmitter();
  @Output() showViewPickUpsEventEmitter = new EventEmitter();
  @Output() showViewDropOffsEventEmitter = new EventEmitter();
  @Output() showViewChangeOversEventEmitter = new EventEmitter();
  @Output() showAddPaxEventEmitter = new EventEmitter();
  @Output() showSetReadyEventEmitter = new EventEmitter();
  @Output() showSetClosedEventEmitter = new EventEmitter();
  @Output() showDeleteTripEventEmitter = new EventEmitter();
  @Output() showDrivenRouteEventEmitter = new EventEmitter();
  @Output() clearIntervalsEventEmitter = new EventEmitter();

  public images = images;
  public language: string = 'es';
  public expeditionStatus = ExpeditionStatus;
  public clientsSelected: IBase[] = [];
  public operatorsSelected: IBase[] = [];
  public servicesSelected: IBase[] = [];
  public routesSelected: IBase[] = [];
  public typesSelected: ExpeditionType[] = [];
  public statusSelected: ExpeditionStatus[] = [];
  public availabilityIdSelected: number | null = null;
  public searchValue: string;

  public bookings: IBooking[];
  public vehicles: IVehicle[];
  public drivers: IUserDriver[];
  
  public showDrivenRoute = false;
  public expandedDrivenRoute = false;

  private languageSubscription: Subscription;
  private queryParamsSub?: Subscription;

  public TooltipPosition = TooltipPosition;

  public interval: any;

  constructor(
    public literalService: LiteralService,
    public utilsService: UtilsService,
    public languageService: LanguageService,
    public tripsService: TripsService,
    public usersService: UsersService,
    public storageService: StorageService,
    public activatedRoute: ActivatedRoute,
    public router: Router,
  ) {}

  get strokeBg(): any {
    return {
      'background-color': `${this.selectedTrip.service && this.selectedTrip.service.color || Colors.TRANSPARENT}`
    };
  }

  get greenBg(): any {
    return {
      'background-color': '#0dedab'
    };
  }

  get purpleBg(): any {
    return {
      'background-color': '#8136db'
    };
  }

  get greyBg(): any {
    return {
      'background-color': '#848ea8'
    };
  }

  async ngOnInit() {
    this.languageSubscription = this.languageService.language$.subscribe(
      (language: string) => {
        this.language = language;
      },
    );

    // URL drives filters/page/search; subscribe so back/forward reactively applies.
    this.queryParamsSub = this.activatedRoute.queryParamMap.subscribe((params) => {
      this.applyQueryParamsToState(params);
    });

    this.interval = setInterval(() => {
      this.getTrips();
    }, 60000);
  }

  private applyQueryParamsToState(params: ParamMap): void {
    const filters: ITripsFilters = hasAnyFilterKey(params, TRIP_FILTER_KEYS)
      ? deserializeTripsFilters(params)
      : {
          selectedClients: [],
          selectedOperators: [],
          selectedTowns: [],
          selectedServices: [],
          selectedRoutes: [],
          selectedTypes: [],
          selectedStatus: DEFAULT_TRIP_STATUS,
          selectedAvailabilityId: null,
        };
    this.clientsSelected = filters.selectedClients;
    this.operatorsSelected = filters.selectedOperators;
    this.servicesSelected = filters.selectedServices;
    this.routesSelected = filters.selectedRoutes;
    this.typesSelected = filters.selectedTypes;
    this.statusSelected = filters.selectedStatus;
    this.availabilityIdSelected = filters.selectedAvailabilityId;

    const pageParam = params.get('page');
    if (pageParam) {
      const page = Number(pageParam);
      if (!isNaN(page) && page > 0) this.trips.pagination.actualPage = page;
    } else {
      this.trips.pagination.actualPage = 1;
    }

    this.searchValue = params.get('search') ?? '';
  }

  async getTrips() {
    this.trips.pagination.numberOfElements = 8;
    this.refreshTripsEventEmitter.emit({
      snapshot: this.activatedRoute.snapshot,
      clientsSelected: this.clientsSelected,
      operatorsSelected: this.operatorsSelected,
      servicesSelected: this.servicesSelected,
      routesSelected: this.routesSelected,
      typesSelected: this.typesSelected,
      statusSelected: this.statusSelected,
      availabilityIdSelected: this.availabilityIdSelected,
      searchValue: this.searchValue
    });
  }

  changePage(event: { isNext: boolean, specificPage?: number }) {
    const current = this.trips.pagination.actualPage;
    const newPage = event.specificPage ?? (event.isNext ? current + 1 : current - 1);
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { page: newPage },
      queryParamsHandling: 'merge',
    });
  }

  getTripDetails(event: any) {
    if (this.showTripDetails) {
      this.goBack();
      this.router.navigate(['/trips'], {
        queryParams: this.activatedRoute.snapshot.queryParams,
      });
    } else {
      this.getTripEventEmitter.emit(event);
    }
  }

  goBack() {
    this.showDrivenRoute = false;
    this.expandedDrivenRoute = false;
    this.showDrivenRouteEventEmitter.emit(this.showDrivenRoute);
    this.clearMap.emit();
  }

  public filter(event: {selectedClients: IBase[], selectedOperators: IBase[], selectedServices: any[], selectedRoutes: any[], selectedTypes: ExpeditionType[], selectedStatus: ExpeditionStatus[]}) {
    const currentFilters = deserializeTripsFilters(this.activatedRoute.snapshot.queryParamMap);
    const filters: ITripsFilters = {
      selectedClients: event?.selectedClients ?? [],
      selectedOperators: event?.selectedOperators ?? [],
      selectedTowns: currentFilters.selectedTowns,
      selectedServices: event?.selectedServices ?? [],
      selectedRoutes: event?.selectedRoutes ?? [],
      selectedTypes: event?.selectedTypes ?? [],
      selectedStatus: event?.selectedStatus ?? [],
      selectedAvailabilityId: currentFilters.selectedAvailabilityId,
    };
    const { selectedAvailabilityId: _availabilityId, ...persistedFilters } = filters;
    this.storageService.setTripsFilters(JSON.stringify(persistedFilters));
    const { towns: _towns, ...filtersWithoutTowns } = serializeTripsFilters(filters);
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { ...filtersWithoutTowns, page: null },
      queryParamsHandling: 'merge',
    });
  };

  search(event: any) {
    const newSearch = event || null;
    const currentSearch = this.activatedRoute.snapshot.queryParamMap.get('search') || null;
    if (newSearch === currentSearch) {
      this.trips.isFetchingTrips = true;
      this.trips.value = [];
      this.getTrips();
      return;
    }
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { search: event || null, page: null },
      queryParamsHandling: 'merge',
    });
  };

  isLate(delay?: number): boolean {
    return delay ? delay > 5 : false;
  }

  isEarly(delay?: number): boolean {
    return delay ? delay < -3 : false;
  }

  shouldHaveStarted(dateTime?: Date): boolean {
    return dateTime ? new Date(dateTime) < new Date() : false;
  }

  getStopColor(index: number) {
    if (this.tripDetails.stops[index].realTime) return this.greyBg;
    if (index === 0) return this.purpleBg;
    if (index === this.tripDetails.stops.length - 1) return this.greenBg;
    return this.strokeBg;
  }

  getLineColor(index: number) {
    if (this.tripDetails.stops.length === index + 1) return '';
    return this.tripDetails.stops[index + 1].realTime ? this.greyBg : this.strokeBg
  }

  getStopTime(stop: ITripStop, isDeparture: boolean): IStopTimeDisplay {
    const estimated = (isDeparture && stop.estimatedDepartureTime) || stop.estimatedTime;
    const theoreticalDescription = this.literalService.get(`trips.tooltips.theoreticalTime.${isDeparture ? 'departure' : 'arrival'}`);
    const theoreticalRow = `${estimated} - ${theoreticalDescription}`;
    const estimatedDisplay: IStopTimeDisplay = { value: estimated, styleClass: 'font-semibold', tooltip: this.utilsService.capitalize(theoreticalDescription) };
    if (this.selectedTrip.status !== ExpeditionStatus.ONGOING && this.selectedTrip.status !== ExpeditionStatus.CLOSED) {
      return estimatedDisplay;
    }
    if (stop.skipped) {
      const value = stop.realTime || stop.estimatedTime;
      return {
        value,
        styleClass: 'font-semibold line-through',
        tooltip: `<s>${value}</s><label> - ${this.literalService.get('trips.tooltips.omitedStops', true)}</label>\n${theoreticalRow}`,
      };
    }
    const real = isDeparture ? stop.realDepartureTime : stop.realTime;
    if (real) {
      return {
        value: real,
        styleClass: 'font-bold',
        tooltip: `<b>${real}</b><label> - ${this.literalService.get(`trips.tooltips.realTime.${isDeparture ? 'departure' : 'arrival'}`)}</label>\n${theoreticalRow}`,
      };
    }
    const foreseen = this.getForeseenTime(stop, isDeparture);
    if (foreseen) {
      return {
        value: foreseen,
        styleClass: 'italic',
        tooltip: `<i>${foreseen}</i><label> - ${this.literalService.get(`trips.tooltips.foreseenTime.${isDeparture ? 'departure' : 'arrival'}`)}</label\n${theoreticalRow}`,
      };
    }
    return estimatedDisplay;
  }

  private getForeseenTime(stop: ITripStop, isDeparture: boolean): string | undefined {
    if (!stop.foreseenTime || !isDeparture) return stop.foreseenTime;
    if (!stop.estimatedDepartureTime) return stop.foreseenTime;
    const delay = this.toMinutes(stop.foreseenTime) - this.toMinutes(stop.estimatedTime);
    return this.addMinutes(stop.estimatedDepartureTime, delay);
  }

  private toMinutes(time: string): number {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  }

  private addMinutes(time: string, minutes: number): string {
    const total = (this.toMinutes(time) + minutes + 1440) % 1440;
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
  }

  onShowDrivenRouteChange(event: any) {
    this.showDrivenRoute = event.target.checked;
    this.showDrivenRouteEventEmitter.emit(this.showDrivenRoute);
  }

  ngOnDestroy(): void {
    this.languageSubscription.unsubscribe();
    this.queryParamsSub?.unsubscribe();
    clearInterval(this.interval);
  }
}
